import React, { useRef, useEffect } from 'react';
import { isVideo } from '../utils/helpers.js';
import { resolveMediaUrl, resolveOverlayCss } from '../utils/styleHelpers.js';
import { GradientAnimator } from './GradientAnimator.jsx';

const EASING_LERP_MAP = {
    'cubic-bezier(0.16, 1, 0.3, 1)': 0.20, // Smooth Spring - silky smooth & responsive
    'ease-out': 0.28,                       // Quick & snappy settle
    'ease-in-out': 0.18,                    // Soft organic deceleration
    'linear': 1.0,                          // Direct 1:1 scroll tracking (zero lag)
    'cubic-bezier(0.34, 1.56, 0.64, 1)': 0.16, // Bounce / Overshoot
    'cubic-bezier(0.68, -0.6, 0.32, 1.6)': 0.14 // Elastic Back Out
};

const resolveParallaxEase = (val) => {
    if (typeof val === 'string' && EASING_LERP_MAP[val] !== undefined) {
        return EASING_LERP_MAP[val];
    }
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
        return Math.max(0.05, Math.min(1.0, num));
    }
    return 0.20;
};

export const BackgroundLayer = ({
    bgImage,
    bgColor,
    backgroundLayers = [],
    backgroundLoop = true,
    overlayOpacity = 0.4,
    overlayColor = '#000000',
    overlayAlways = false,
    overlayType = 'solid',
    overlayGradientAngle = '180',
    overlayGradientStart,
    overlayGradientMid,
    overlayGradientEnd,
    overlayGradientStartOpacity,
    overlayGradientMidOpacity,
    overlayGradientEndOpacity,
    overlayGradientStartPos,
    overlayGradientMidPos,
    overlayGradientEndPos,
    overlayGradientAnimate = false,
    overlayGradientAnimateMode = 'translate',
    overlayGradientAnimateSpeed = '8',
    overlayBlendMode,
    bgSize = 'cover',
    bgPosition = 'center',
    bgRepeat = 'no-repeat',
    bgDesaturate = 0,
    bgFilter,
    bgTint,
    bgTintOpacity = 0.4,
    bgTintBlendMode = 'multiply',
    bgHoverInteraction = 'none',
    bgClickInteraction = 'none',
    bgTransitionDuration = '0.5s',
    bgTransitionTiming = 'cubic-bezier(0.4, 0, 0.2, 1)',
    parallax = false,
    parallaxSpeed = 0.5,
    parallaxSmoothing = 'cubic-bezier(0.16, 1, 0.3, 1)',
    parallaxZoomMode = 'standard',
    parallaxDirection = 'vertical',
    parallaxMobile = 'reduced'
}) => {
    const containerRef = useRef(null);
    const bgInnerRef = useRef(null);
    const currentYRef = useRef(0);
    const currentXRef = useRef(0);
    const targetYRef = useRef(0);
    const targetXRef = useRef(0);

    // Multi-layer parallax refs & tracking state
    const layerRefs = useRef([]);
    const layerStateRef = useRef([]);

    const safeMultiLayers = Array.isArray(backgroundLayers) ? backgroundLayers.filter(l => l && l.image) : [];
    const hasMultiLayers = safeMultiLayers.length > 0;

    const resolvedBgImage = resolveMediaUrl(bgImage);
    const isVid = isVideo(resolvedBgImage);
    const speedVal = parseFloat(parallaxSpeed ?? 0.5);
    const easeVal = resolveParallaxEase(parallaxSmoothing);

    const isMobile = typeof window !== 'undefined' && (window.innerWidth <= 768 || ('ontouchstart' in window && window.innerWidth <= 1024));
    // On mobile devices, background-attachment: fixed causes severe rendering bugs/jitter in WebKit/Blink; use GPU translate3d parallax instead
    const useFixedAttachment = !isMobile && parallax && (parallaxZoomMode === 'fixed');

    const isCustomParallax = parallax && !useFixedAttachment && !isVid;
    const zoomMultiplier = parallaxZoomMode === 'minimal' ? 0.5 : (parallaxZoomMode === 'extra' ? 1.4 : 1.0);
    const mobileScale = isMobile ? (parallaxMobile === 'disabled' ? 0 : (parallaxMobile === 'enabled' ? 1.0 : 0.5)) : 1.0;
    // Responsive travel calculation: higher speed slider = proportionally greater travel; subtle at 0.1, responsive at 0.5, deep at 1.0+
    const baseTravel = Math.max(15, Math.round(speedVal * 110 * zoomMultiplier * mobileScale));
    const maxTravelPx = Math.min(260, baseTravel);
    // Overscan guaranteed to exceed max travel with safety margin so empty edges are never revealed
    const overscanPx = isCustomParallax ? Math.round(maxTravelPx * 1.25 + 20) : 0;

    useEffect(() => {
        const needsParallax = (parallax && !useFixedAttachment && !isVid) || hasMultiLayers;
        if (!needsParallax) {
            if (bgInnerRef.current) {
                bgInnerRef.current.style.transform = '';
            }
            (layerRefs.current || []).forEach(el => {
                if (el) el.style.transform = '';
            });
            return;
        }
        const container = containerRef.current;
        const bgInner = bgInnerRef.current;
        if (!container) return;

        // Ensure layerStateRef matches current safeMultiLayers length
        layerStateRef.current = safeMultiLayers.map((layer, idx) => {
            const existing = layerStateRef.current[idx];
            return {
                currentY: existing ? existing.currentY : 0,
                currentX: existing ? existing.currentX : 0,
                targetY: 0,
                targetX: 0,
                easeVal: resolveParallaxEase(layer.smoothing || 'cubic-bezier(0.16, 1, 0.3, 1)')
            };
        });

        let rafId = null;
        let isRunning = false;

        const getDirMultipliers = (dir) => {
            if (dir === 'reverse' || dir === 'up') return { y: -1, x: 0 };
            if (dir === 'horizontal' || dir === 'right') return { y: 0, x: 1 };
            if (dir === 'horizontal-reverse' || dir === 'left') return { y: 0, x: -1 };
            if (dir === 'diagonal' || dir === 'diagonal-up-right') return { y: -0.7, x: 0.7 };
            if (dir === 'diagonal-up-left') return { y: -0.7, x: -0.7 };
            return { y: 1, x: 0 };
        };

        const calcTarget = () => {
            if (!container) return;
            const rect = container.getBoundingClientRect();
            const vh = window.innerHeight || 800;

            // Viewport visibility check: skip computations if well outside the viewport (+200px buffer)
            if (rect.bottom < -200 || rect.top > vh + 200) {
                return;
            }

            const elCenter = rect.top + rect.height / 2;
            const vpCenter = vh / 2;
            const halfSpan = (vh + rect.height) / 2;

            const normDist = halfSpan > 0 ? (vpCenter - elCenter) / halfSpan : 0;
            const clampedProgress = Math.max(-1, Math.min(1, normDist));

            // 1. Calculate Base Layer Target
            if (isCustomParallax) {
                const baseDir = getDirMultipliers(parallaxDirection);
                targetYRef.current = clampedProgress * maxTravelPx * baseDir.y;
                targetXRef.current = clampedProgress * maxTravelPx * baseDir.x;
            }

            // 2. Calculate Targets for each multi-layer
            safeMultiLayers.forEach((layer, idx) => {
                const lSpeed = parseFloat(layer.speed ?? 0.5);
                const lZoom = layer.zoomMode === 'minimal' ? 0.5 : (layer.zoomMode === 'extra' ? 1.4 : 1.0);
                const lBaseTravel = Math.max(15, Math.round(lSpeed * 110 * lZoom * mobileScale));
                const lMaxTravel = Math.min(320, lBaseTravel);
                const lDir = getDirMultipliers(layer.direction || 'vertical');

                if (layerStateRef.current[idx]) {
                    layerStateRef.current[idx].targetY = clampedProgress * lMaxTravel * lDir.y;
                    layerStateRef.current[idx].targetX = clampedProgress * lMaxTravel * lDir.x;
                }
            });

            if (!isRunning) {
                isRunning = true;
                rafId = requestAnimationFrame(tick);
            }
        };

        const tick = () => {
            let stillAnimating = false;

            // 1. Animate Base Layer
            if (isCustomParallax && bgInner) {
                if (easeVal >= 0.95) {
                    currentYRef.current = targetYRef.current;
                    currentXRef.current = targetXRef.current;
                    bgInner.style.transform = `translate3d(${currentXRef.current.toFixed(2)}px, ${currentYRef.current.toFixed(2)}px, 0)`;
                } else {
                    const diffY = targetYRef.current - currentYRef.current;
                    const diffX = targetXRef.current - currentXRef.current;
                    if (Math.abs(diffY) > 0.04 || Math.abs(diffX) > 0.04) {
                        currentYRef.current += diffY * easeVal;
                        currentXRef.current += diffX * easeVal;
                        stillAnimating = true;
                    } else {
                        currentYRef.current = targetYRef.current;
                        currentXRef.current = targetXRef.current;
                    }
                    bgInner.style.transform = `translate3d(${currentXRef.current.toFixed(2)}px, ${currentYRef.current.toFixed(2)}px, 0)`;
                }
            }

            // 2. Animate each multi-layer
            safeMultiLayers.forEach((layer, idx) => {
                const el = layerRefs.current[idx];
                const state = layerStateRef.current[idx];
                if (!el || !state) return;

                const lEase = state.easeVal || easeVal;
                if (lEase >= 0.95) {
                    state.currentY = state.targetY;
                    state.currentX = state.targetX;
                    el.style.transform = `translate3d(${state.currentX.toFixed(2)}px, ${state.currentY.toFixed(2)}px, 0)`;
                } else {
                    const dY = state.targetY - state.currentY;
                    const dX = state.targetX - state.currentX;
                    if (Math.abs(dY) > 0.04 || Math.abs(dX) > 0.04) {
                        state.currentY += dY * lEase;
                        state.currentX += dX * lEase;
                        stillAnimating = true;
                    } else {
                        state.currentY = state.targetY;
                        state.currentX = state.targetX;
                    }
                    el.style.transform = `translate3d(${state.currentX.toFixed(2)}px, ${state.currentY.toFixed(2)}px, 0)`;
                }
            });

            if (stillAnimating) {
                rafId = requestAnimationFrame(tick);
            } else {
                isRunning = false;
            }
        };

        window.addEventListener('scroll', calcTarget, { passive: true });
        window.addEventListener('resize', calcTarget, { passive: true });
        calcTarget();

        return () => {
            window.removeEventListener('scroll', calcTarget);
            window.removeEventListener('resize', calcTarget);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [parallax, speedVal, easeVal, useFixedAttachment, isVid, maxTravelPx, parallaxDirection, parallaxMobile, hasMultiLayers, safeMultiLayers]);

    const [isHovered, setIsHovered] = React.useState(false);
    const [isToggled, setIsToggled] = React.useState(false);

    useEffect(() => {
        if (!bgHoverInteraction || bgHoverInteraction === 'none') return;
        const container = containerRef.current;
        if (!container) return;
        const parent = container.parentElement;
        if (!parent) return;

        const onEnter = () => setIsHovered(true);
        const onLeave = () => setIsHovered(false);

        parent.addEventListener('mouseenter', onEnter);
        parent.addEventListener('mouseleave', onLeave);
        return () => {
            parent.removeEventListener('mouseenter', onEnter);
            parent.removeEventListener('mouseleave', onLeave);
        };
    }, [bgHoverInteraction]);

    useEffect(() => {
        if (!bgClickInteraction || bgClickInteraction === 'none') return;
        const container = containerRef.current;
        if (!container) return;
        const parent = container.parentElement;
        if (!parent) return;

        const onClick = (e) => {
            if (e.target.closest('a, button, input, textarea, select, [data-editable], .admin-btn, .editable-btn, .customizer-drawer, .draggable-toolbar')) return;
            if (bgClickInteraction === 'toggle' || bgClickInteraction === 'color-on-click') {
                setIsToggled(prev => !prev);
            }
        };

        parent.addEventListener('click', onClick);
        return () => {
            parent.removeEventListener('click', onClick);
        };
    }, [bgClickInteraction]);

    let computedFilter = bgFilter && bgFilter !== 'none' ? bgFilter : '';
    if (!computedFilter) {
        const parts = [];
        const desatVal = bgDesaturate !== undefined && bgDesaturate !== '' ? parseFloat(bgDesaturate) : 0;
        if (!isNaN(desatVal) && desatVal > 0) {
            parts.push(`grayscale(${desatVal}%)`);
        }
        if (parts.length > 0) computedFilter = parts.join(' ');
    }

    const isColorRevealed = isToggled || (isHovered && (bgHoverInteraction === 'color-on-hover' || bgHoverInteraction === 'zoom-color-on-hover' || bgHoverInteraction === 'zoom-color'));
    const isDesatHover = !isToggled && isHovered && bgHoverInteraction === 'desaturate-on-hover';
    const isTintBoosted = !isToggled && isHovered && bgHoverInteraction === 'boost-tint-on-hover';
    const isZoomHover = isHovered && (bgHoverInteraction === 'zoom-color-on-hover' || bgHoverInteraction === 'zoom-color' || bgHoverInteraction === 'zoom');

    let activeFilter = computedFilter;
    if (isColorRevealed) {
        activeFilter = 'none';
    } else if (isDesatHover) {
        activeFilter = 'grayscale(100%)';
    }

    const hasTint = Boolean(bgTint && bgTint !== 'transparent');
    const baseTintOpacity = (bgTintOpacity !== undefined && bgTintOpacity !== '' && !isNaN(parseFloat(bgTintOpacity)))
        ? parseFloat(bgTintOpacity)
        : 0.4;
    let activeTintOpacity = baseTintOpacity;
    if (isColorRevealed) {
        activeTintOpacity = 0;
    } else if (isTintBoosted) {
        activeTintOpacity = Math.min(1, baseTintOpacity * 1.5 + 0.2);
    }
    const tintBlend = bgTintBlendMode || 'multiply';

    const transDur = bgTransitionDuration || '0.5s';
    const transTiming = bgTransitionTiming || 'cubic-bezier(0.4, 0, 0.2, 1)';

    const { background: overlayBackground, showOverlay } = resolveOverlayCss({
        overlayOpacity,
        overlayColor,
        overlayAlways,
        overlayType,
        overlayGradientAngle,
        overlayGradientStart,
        overlayGradientMid,
        overlayGradientEnd,
        overlayGradientStartOpacity,
        overlayGradientMidOpacity,
        overlayGradientEndOpacity,
        overlayGradientStartPos,
        overlayGradientMidPos,
        overlayGradientEndPos,
    });

    const overlayStyle = {
        position: 'absolute',
        inset: 0,
        background: overlayBackground,
        mixBlendMode: overlayBlendMode || undefined,
        zIndex: 1,
        pointerEvents: 'none'
    };

    const isGradientBg = typeof bgColor === 'string' && bgColor.includes('gradient(');

    return (
        <div ref={containerRef} style={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', ...(isGradientBg ? { background: bgColor } : { backgroundColor: bgColor }) }}>
            {/* Parallax Transform Layer - purely manages GPU-accelerated translate3d with zero CSS transition interference */}
            <div
                ref={bgInnerRef}
                style={{
                    position: 'absolute',
                    top: isCustomParallax ? `-${overscanPx}px` : 0,
                    bottom: isCustomParallax ? `-${overscanPx}px` : 0,
                    left: (isCustomParallax && (parallaxDirection?.includes('horizontal') || parallaxDirection?.includes('diagonal'))) ? `-${overscanPx}px` : 0,
                    right: (isCustomParallax && (parallaxDirection?.includes('horizontal') || parallaxDirection?.includes('diagonal'))) ? `-${overscanPx}px` : 0,
                    width: (isCustomParallax && (parallaxDirection?.includes('horizontal') || parallaxDirection?.includes('diagonal'))) ? `calc(100% + ${overscanPx * 2}px)` : '100%',
                    height: isCustomParallax ? `calc(100% + ${overscanPx * 2}px)` : '100%',
                    willChange: isCustomParallax ? 'transform' : undefined,
                    transition: 'none',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                }}
            >
                {/* Media Layer (Image or Video) - handles sizing, filter effects, and hover zoom independently */}
                {isVid ? (
                    <video
                        autoPlay
                        loop={backgroundLoop !== false}
                        muted
                        playsInline
                        src={resolvedBgImage}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: bgSize === 'contain' ? 'contain' : 'cover',
                            objectPosition: bgPosition || 'center',
                            filter: activeFilter || 'none',
                            WebkitFilter: activeFilter || 'none',
                            transform: isZoomHover ? 'scale(1.04)' : undefined,
                            transition: `filter ${transDur} ${transTiming}, -webkit-filter ${transDur} ${transTiming}, transform ${transDur} ${transTiming}`,
                        }}
                    />
                ) : (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            backgroundImage: resolvedBgImage ? `url('${resolvedBgImage}')` : 'none',
                            backgroundAttachment: useFixedAttachment ? 'fixed' : 'scroll',
                            backgroundSize: bgSize || 'cover',
                            backgroundPosition: bgPosition || 'center',
                            backgroundRepeat: bgRepeat || 'no-repeat',
                            filter: activeFilter || 'none',
                            WebkitFilter: activeFilter || 'none',
                            transform: isZoomHover ? 'scale(1.04)' : undefined,
                            transformOrigin: 'center center',
                            transition: `filter ${transDur} ${transTiming}, -webkit-filter ${transDur} ${transTiming}, transform ${transDur} ${transTiming}`,
                        }}
                    />
                )}
            </div>

            {/* Multi-Layer Parallax Background Planes */}
            {safeMultiLayers.map((layer, idx) => {
                const layerImg = resolveMediaUrl(layer.image);
                if (!layerImg) return null;
                const lSpeed = parseFloat(layer.speed ?? 0.5);
                const lZoom = layer.zoomMode === 'minimal' ? 0.5 : (layer.zoomMode === 'extra' ? 1.4 : 1.0);
                const lBaseTravel = Math.max(15, Math.round(lSpeed * 110 * lZoom * mobileScale));
                const lMaxTravel = Math.min(320, lBaseTravel);
                const lOverscan = Math.round(lMaxTravel * 1.25 + 24);
                const lDir = layer.direction || 'vertical';
                const isHorizOrDiag = lDir.includes('horizontal') || lDir.includes('diagonal');

                return (
                    <div
                        key={layer.id || idx}
                        ref={el => { layerRefs.current[idx] = el; }}
                        style={{
                            position: 'absolute',
                            top: `-${lOverscan}px`,
                            bottom: `-${lOverscan}px`,
                            left: isHorizOrDiag ? `-${lOverscan}px` : 0,
                            right: isHorizOrDiag ? `-${lOverscan}px` : 0,
                            width: isHorizOrDiag ? `calc(100% + ${lOverscan * 2}px)` : '100%',
                            height: `calc(100% + ${lOverscan * 2}px)`,
                            willChange: 'transform',
                            transition: 'none',
                            backfaceVisibility: 'hidden',
                            WebkitBackfaceVisibility: 'hidden',
                            zIndex: idx + 1,
                            pointerEvents: 'none',
                            opacity: layer.opacity !== undefined ? layer.opacity : 1,
                            mixBlendMode: layer.blendMode || 'normal',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                backgroundImage: `url('${layerImg}')`,
                                backgroundSize: layer.size || 'cover',
                                backgroundPosition: layer.position || 'center',
                                backgroundRepeat: layer.repeat || 'no-repeat',
                                filter: layer.filter || 'none',
                                WebkitFilter: layer.filter || 'none',
                            }}
                        />
                    </div>
                );
            })}

            {/* Colour Tint Overlay Layer */}
            {hasTint && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: bgTint,
                        opacity: activeTintOpacity,
                        mixBlendMode: tintBlend,
                        zIndex: 1,
                        pointerEvents: 'none',
                        transition: `opacity ${transDur} ${transTiming}, background-color ${transDur} ${transTiming}`,
                    }}
                />
            )}

            {/* Dark/Color/Gradient Overlay */}
            {showOverlay && (
                (overlayType === 'animatedGradient' || overlayGradientAnimate) ? (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', overflow: 'hidden', opacity: overlayOpacity }}>
                        <GradientAnimator
                            prefix="overlay"
                            fixed={false}
                            customObj={{
                                heroGradientStart: overlayGradientStart || overlayColor || '#000000',
                                heroGradientMid: overlayGradientMid || null,
                                heroGradientEnd: overlayGradientEnd || '#000000',
                                heroGradientAngle: overlayGradientAngle || '180',
                                heroGradientAnimateSpeed: overlayGradientAnimateSpeed || '8',
                                heroGradientAnimateMode: overlayGradientAnimateMode || 'translate',
                            }}
                        />
                    </div>
                ) : (
                    <div style={overlayStyle} />
                )
            )}
        </div>
    );
};

export default BackgroundLayer;
